"use client"

import { AlertTriangle } from "lucide-react"
import { Button } from "../ui/button"

export interface UnsavedChangesPromptProps {
  /** Callback when user confirms discarding changes */
  onDiscard: () => void
  /** Callback when user wants to continue editing */
  onContinue: () => void
  /** Custom title text */
  title?: string
  /** Custom message text */
  message?: string
  /** Custom discard button text */
  discardLabel?: string
  /** Custom continue button text */
  continueLabel?: string
}

/**
 * Inline discard confirmation UI component.
 * Used within FormDialog to replace content when user tries to close with unsaved changes.
 *
 * @example
 * ```tsx
 * {showDiscardPrompt ? (
 *   <UnsavedChangesPrompt
 *     onDiscard={() => {
 *       form.reset()
 *       onOpenChange(false)
 *     }}
 *     onContinue={() => setShowDiscardPrompt(false)}
 *   />
 * ) : (
 *   <FormContent />
 * )}
 * ```
 */
export const UnsavedChangesPrompt = ({
  onDiscard,
  onContinue,
  title = "Discard changes?",
  message = "You have unsaved changes that will be lost.",
  discardLabel = "Discard",
  continueLabel = "Continue Editing",
}: UnsavedChangesPromptProps) => (
  <div className="flex flex-col items-center gap-4 py-8">
    <AlertTriangle className="h-12 w-12 text-amber-500" />
    <div className="text-center">
      <h3 className="text-lg font-semibold">{title}</h3>
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
    <div className="flex gap-2">
      <Button variant="outline" onClick={onContinue}>
        {continueLabel}
      </Button>
      <Button variant="destructive" onClick={onDiscard}>
        {discardLabel}
      </Button>
    </div>
  </div>
)
